import '../types';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnDef } from '@tanstack/react-table';

export const MAX_STICKY_COLUMNS = 4;

function countVisible<T>(columns: ColumnDef<T>[]) {
	return columns.filter((col) => col.meta?.visible ?? true).length;
}

export function couldColumnBeSticky<T>(
	columns: ColumnDef<T>[],
	colIndex: number
) {
	const thisColumn = columns[colIndex]!;

	const numVisible = countVisible(columns);

	const previousColumns = columns.slice(0, colIndex);
	const previousNumVisible = countVisible(previousColumns);

	// if there are no visible columns, we are done
	if (numVisible === 0) return false;

	if (
		(thisColumn.meta?.visible ?? true) &&
		previousNumVisible < MAX_STICKY_COLUMNS
	) {
		return true;
	}

	return false;
}

export function setColumnSticky<T>({
	columns,
	columnId,
	sticky,
	onLastSticky,
	minSticky,
}: {
	columns: ColumnDef<T>[];
	columnId: string;
	sticky: boolean;
	onLastSticky?: () => void;
	minSticky: number | null;
}) {
	const thisIdx = columns.findIndex((c) => c.id === columnId);

	if (thisIdx === -1) {
		console.error('did not find column with id', columnId);
		return columns;
	}

	if (
		minSticky &&
		sticky === false &&
		columns.filter((c) => c.meta?.sticky).length <= minSticky
	) {
		onLastSticky?.();
		return columns;
	}

	const editedColumn = { ...columns[thisIdx]! };
	editedColumn.meta = { ...editedColumn.meta, sticky };

	const stickyColumns = columns.filter(
		(c, i) => c.meta?.sticky && i !== thisIdx
	);
	const nonStickyColumns = columns.filter(
		(c, i) => !c.meta?.sticky && i !== thisIdx
	);

	const updatedColumns = [...stickyColumns, editedColumn, ...nonStickyColumns];

	enforceColumnRules({
		columns: updatedColumns,
		original: columns,
		minSticky,
	});

	return updatedColumns;
}

export function setColumnVisible<T>({
	columns,
	columnId,
	visible,
	onLastVisible,
	minSticky,
}: {
	columns: ColumnDef<T>[];
	columnId: string;
	visible: boolean;
	onLastVisible?: () => void;
	minSticky: number | null;
}) {
	// require at least three visible column checked
	if (
		visible === false &&
		columns.filter((c) => c.meta?.visible ?? true).length <= 3
	) {
		onLastVisible?.();
		return columns;
	}

	const newColumns = columns.map((c) =>
		c.id === columnId
			? {
					...c,
					meta: {
						...c.meta,
						visible,
					},
				}
			: c
	);

	enforceColumnRules({
		columns: newColumns,
		original: columns,
		minSticky,
	});

	return newColumns;
}

export function toggleAllColumnsVisible<T>({
	columns,
	minVisible = 3,
	minSticky = null,
}: {
	columns: ColumnDef<T>[];
	minVisible?: number;
	minSticky: number | null;
}) {
	// Determine the current visibility state of columns
	const visibleColumnsCount = columns.filter(
		(c) => c.meta?.visible ?? true
	).length;
	const allVisible = visibleColumnsCount === columns.length;

	// If all columns are visible or if making all columns non-visible would violate the minVisible constraint, toggle accordingly
	if (
		allVisible ||
		visibleColumnsCount - columns.length + minVisible < minVisible
	) {
		return columns.map((c, index) => ({
			...c,
			meta: {
				...c.meta,
				// Keep the first minVisible columns visible, toggle others based on the allVisible flag
				visible: index < minVisible ? true : !allVisible,
			},
		}));
	} else {
		// If some columns are not visible, make all columns visible
		const newColumns = columns.map((c) => ({
			...c,
			meta: {
				...c.meta,
				visible: true,
			},
		}));

		// Apply column rules after toggling visibility
		enforceColumnRules({
			columns: newColumns,
			original: columns,
			minSticky,
		});

		return newColumns;
	}
}

function insert<T>(array: T[], index: number, ...items: T[]) {
	array.splice(index, 0, ...items);
}

export function moveColumn<T>({
	dragEndEvent: { active, over },
	columns,
	minSticky,
}: {
	columns: ColumnDef<T>[];
	dragEndEvent: DragEndEvent;
	minSticky: number | null;
}) {
	if (!over || active.id === over.id) {
		return columns;
	}

	const oldIndex = columns.findIndex((column) => column.id === active.id);
	const newIndex = columns.findIndex((column) => column.id === over.id);

	const unmovableColumns = columns
		.map((c, i) => ({ index: i, column: c }))
		.filter((c) => c.column.meta?.disableOrdering);

	const updatedColumns = arrayMove(columns, oldIndex, newIndex).filter(
		(c) => !c.meta?.disableOrdering
	);

	for (const column of unmovableColumns) {
		insert(updatedColumns, column.index, column.column);
	}

	enforceColumnRules({
		columns: updatedColumns,
		original: columns,
		minSticky,
	});

	return updatedColumns;
}

/** Make invisible columns non-sticky. */
function enforceNotVisibleImpliesNotSticky<T>(columns: ColumnDef<T>[]) {
	for (const column of columns) {
		if (column.meta?.visible === false) {
			column.meta.sticky = false;
		}
	}
}

/**
 * Make sure that the first sticky column is the first visible column
 */
function enforceStickyStart<T>(columns: ColumnDef<T>[]) {
	let inStickyZone = true;

	for (const column of columns) {
		column.meta ??= {};
		if (column.meta.visible === false) {
			continue;
		}
		if (!column.meta.sticky) {
			inStickyZone = false;
		}
		if (column.meta.sticky && !inStickyZone) {
			column.meta.sticky = false;
		}
	}
}

/** Unsticks columns that are too far from the beginning to be sticky. */
export function enforceMaxMinSticky<T>(
	columns: ColumnDef<T>[],
	minSticky: number | null
) {
	let couldBeStickySoFar = 0;

	for (const column of columns) {
		if (column.meta?.visible) {
			if (couldBeStickySoFar >= MAX_STICKY_COLUMNS) {
				column.meta.sticky = false;
			} else {
				couldBeStickySoFar++;
			}
		}
	}

	if (minSticky) {
		let enoughSticky = columns.filter((c) => c.meta?.sticky).length;

		for (const column of columns) {
			if (
				column.meta?.visible &&
				!column.meta.disableConfig &&
				!column.meta.sticky &&
				enoughSticky < minSticky
			) {
				column.meta.sticky = true;
				enoughSticky++;
			}
		}
	}
}

/** Fills in all gaps between the beginning and last sticky column. */
function enforceNoGapsInSticky<T>(columns: ColumnDef<T>[]) {
	let inStickyZone = false;

	for (let i = columns.length - 1; i >= 0; --i) {
		const column = columns[i]!;
		column.meta ??= {};
		if (column.meta.visible !== false) {
			if (inStickyZone) {
				column.meta.sticky = true;
			} else if (column.meta.sticky) {
				inStickyZone = true;
			}
		}
	}
}

function enforceDisableOrderBy<T>(
	newCols: ColumnDef<T>[],
	origCols: ColumnDef<T>[]
) {
	for (const [origI, origCol] of origCols.entries()) {
		if (origCol.meta?.disableOrdering) {
			const newI = newCols.findIndex((c) => c.id === origCol.id);
			if (origI !== newI) {
				const [newCol] = newCols.splice(newI, 1);
				newCols.splice(origI, 0, newCol!);
			}
		}
	}
}

export function enforceColumnRules<T>({
	columns,
	original,
	minSticky,
}: {
	columns: ColumnDef<T>[];
	original: ColumnDef<T>[];
	/**
	 * While setting this to `number | null` is the functionally the same as `?: number`,
	 * null over undefined ensures that the caller will always pass in the value
	 */
	minSticky: number | null;
}) {
	enforceDisableOrderBy(columns, original);
	enforceMaxMinSticky(columns, minSticky);
	enforceNoGapsInSticky(columns);
	enforceStickyStart(columns);
	enforceNotVisibleImpliesNotSticky(columns);
}
